import { StateError } from '../errors';
import { rngInt } from '../math/rng';
import type { RuleModule } from '../engine/registry';
import { OK, reject, type ActionSpec, type BackRef, type GameEventDraft, type GameRuntime, type GameState } from '../engine/state';
import { enterPhase } from '../engine/phase';
import { liveBoard } from '../engine/liveBoard';
import { bagTotal } from '../engine/gameDef';
import { publicVp } from './victory';
import type { HexId } from '../board/axial';
import type { SeatIdx } from '../types';

// The Bandit (outline §9): on a 7 — no harvest; single-pass half-discard for hands
// STRICTLY over the threshold (advance cards never count, outline §10); the roller MUST
// move the Bandit to a DIFFERENT land hex (desert legal); then one blind seeded steal
// from a chosen adjacent victim (zero-card victims yield nothing, no substitute draw;
// never self; outline §14.3/6) who is not shielded by the Friendly Bandit threshold
// (`stealCandidates` below). Nothing — trading included — interposes until complete
// (outline §14.5): the phase machine simply offers no other verbs. Militia reuses the
// move+steal by entering BANDIT_MOVE via 'MILITIA' with a back-ref (H1-S7). Since D182 the
// Friendly Bandit also closes the hexes a shielded seat builds on (`banditTargets`).

export interface BanditBag {
  hex: HexId;
}

const bag = (state: GameState): BanditBag => state.bags['bandit'] as BanditBag;

/** The §6.7 per-map override, else the config's threshold (exported so T-NET-032 pins the read). */
export function discardThreshold(env: GameRuntime): number {
  return env.map.overrides.discardOver ?? env.cfg.engine.discardOver;
}

/**
 * The Friendly Bandit (rules change 2026-08-18, always on): a seat whose PUBLIC victory
 * points are at or below `cfg.engine.friendlyRobberVp` cannot be robbed. PUBLIC, never
 * true VP — a steal's legality is visible to the whole table, so judging by hidden
 * Landmarks would leak them through the legal-action set (brief invariant 5). A threshold
 * ≤ 0 disables the protection entirely, which is exactly classic behaviour.
 */
export function robbable(state: GameState, env: GameRuntime, seat: SeatIdx): boolean {
  const floor = env.cfg.engine.friendlyRobberVp;
  if (floor <= 0) return true;
  return publicVp(state, env, seat) > floor;
}

/**
 * Where may the Bandit be placed? The ONE chokepoint — `enumerate` maps it and `validate`
 * checks membership, so a client's highlights and the server's ruling can never disagree.
 *
 * The Friendly Bandit shields a low-VP seat from being STOLEN from; D182 extends it to
 * BLOCKING, because blocking is robbing by another name — the tile stops paying, which costs
 * the protected player what a steal would and needs no victim to pick, so protecting only the
 * steal left the same door open indirectly. Every hex a shielded seat builds on is therefore
 * closed.
 *
 * Two boundaries the rule needs:
 *  - The mover's OWN buildings never close a hex. The mover is already outside the protection
 *    system everywhere (`adjacentSeats` skips them — a blocker is never their own victim), and
 *    parking the Bandit on your own low-value tile is the standard polite move.
 *  - If that leaves NOWHERE, the shield yields rather than the phase deadlocking. In the first
 *    turns every seat sits at exactly 2 VP, so on a small board the closures can cover every
 *    built hex at once; BANDIT_MOVE is mandatory and un-skippable (outline §14.5), so a legal
 *    destination has to exist. Falling back to the classic rule is the only outcome that is
 *    still a game.
 */
export function banditTargets(state: GameState, env: GameRuntime, mover: SeatIdx): HexId[] {
  const cur = bag(state).hex;
  // the LIVE board (H20): a hex revealed from fog is land the Bandit may stand on
  const all = [...liveBoard(state, env).hexes.keys()].filter((h) => h !== cur); // outline §14.3: it must MOVE
  if (env.cfg.engine.friendlyRobberVp <= 0) return all; // protection off ⇒ classic behaviour
  const open = all.filter((hex) => !shielded(state, env, hex, mover));
  return open.length > 0 ? open : all;
}

/** Does a seat the Friendly Bandit protects build on this hex? */
function shielded(state: GameState, env: GameRuntime, hex: HexId, mover: SeatIdx): boolean {
  return adjacentSeats(state, env, hex, mover).some((seat) => !robbable(state, env, seat));
}

/** Steal candidates: seats (≠ mover, non-neutral, unresigned or not — resigned seats STAY
 *  robbable, D5) with a vertex building adjacent to the bandit's hex, MINUS those the
 *  Friendly Bandit protects. The one chokepoint: STEAL_PICK enumerate/validate and the
 *  MOVE_BANDIT tail all read it, so an all-protected hex is an ordinary no-victims outcome
 *  (outline §9). Since D182 such a hex is not normally a legal DESTINATION either — see
 *  `banditTargets` — but it still can be when the shield has to yield. */
export function stealCandidates(state: GameState, env: GameRuntime, hex: HexId, mover: SeatIdx): SeatIdx[] {
  return adjacentSeats(state, env, hex, mover).filter((seat) => robbable(state, env, seat));
}

/** The raw adjacency — everyone the Bandit's hex touches, protected or not. The rejection
 *  below needs it to say WHY a shielded neighbour is not a victim (D27); exported (H20-S0b)
 *  so the sea raider's closure reads the same neighbours the Bandit's does. */
export function adjacentSeats(state: GameState, env: GameRuntime, hex: HexId, mover: SeatIdx): SeatIdx[] {
  const seats = new Set<SeatIdx>();
  const board = liveBoard(state, env);
  for (const [vid, placement] of Object.entries(state.board.vertices)) {
    if (placement.owner === 'neutral' || placement.owner === mover) continue; // blockers never robbed (outline §13.1)
    if (board.vertices.get(vid)?.hexes.includes(hex)) seats.add(placement.owner);
  }
  return [...seats].sort((a, b) => a - b);
}

/** Resolve a sea map's bandit start against the dealt land (smallest id wins a tie). */
function banditStartOn(hexes: ReadonlyMap<HexId, { id: HexId; tile: string; disc: number | null }>, start: 'desert' | { disc: number } | { q: number; r: number }): HexId {
  const ids =
    start === 'desert'
      ? [...hexes.values()].filter((h) => h.tile === 'desert').map((h) => h.id)
      : 'disc' in start
        ? [...hexes.values()].filter((h) => h.disc === start.disc).map((h) => h.id)
        : [`h${start.q},${start.r}`].filter((id) => hexes.has(id));
  if (ids.length === 0) throw new StateError(`bandit: nowhere to start (${JSON.stringify(start)}) — map validation should have caught this`);
  return ids.sort()[0]!;
}

/** The module's 7-resolution core — exported so fixtures force sevens (T-ENG-031..038). */
export function resolveSevenRoll(state: GameState, env: GameRuntime, emit: (e: GameEventDraft) => void): void {
  if (state.phase.t !== 'PRE_ROLL') throw new StateError('resolveSevenRoll outside PRE_ROLL');
  const roller = state.phase.seat;
  const threshold = discardThreshold(env);
  const owed: Record<number, number> = {};
  state.seats.forEach((seat, idx) => {
    const n = bagTotal(seat.hand); // resource cards only — advance cards never count (outline §10)
    if (n > threshold) owed[idx] = Math.floor(n / 2);
  });
  if (Object.keys(owed).length > 0) {
    emit({ kind: 'DISCARD_OWED', vis: 'public', owed: { ...owed } });
    enterPhase(state, env, { t: 'DISCARD', seat: roller, owed }, emit);
  } else {
    enterPhase(state, env, { t: 'BANDIT_MOVE', seat: roller, via: 'ROLL7', back: 'ACTION' }, emit);
  }
}

/** Enter the move+steal tail (militia reuses this with via='MILITIA' — outline §10). */
export function enterBanditMove(state: GameState, env: GameRuntime, seat: SeatIdx, via: 'ROLL7' | 'MILITIA', back: BackRef, emit: (e: GameEventDraft) => void): void {
  enterPhase(state, env, { t: 'BANDIT_MOVE', seat, via, back }, emit);
}

/** Who a raider's hex exposes: the robbable victims, and the raw neighbours the refusal names. */
export interface StealVictims {
  candidates(state: GameState, env: GameRuntime, hex: HexId, mover: SeatIdx): SeatIdx[];
  adjacent(state: GameState, env: GameRuntime, hex: HexId, mover: SeatIdx): SeatIdx[];
}

/**
 * The steal step (outline §9/§14.6), built over a victim rule and exported (H20-S0b) so the
 * sea raider can reuse it rather than restate it: the refusal reasons, the one blind seeded
 * draw (plan §9.3) and the return to the interrupted window are one implementation, whichever
 * raider moved — only WHO stands to be robbed differs.
 */
export function makeStealPickSpec(victims: StealVictims): ActionSpec {
  return {
    enumerate(state, env, seat) {
      if (state.phase.t !== 'STEAL_PICK' || state.phase.seat !== seat) return null;
      return victims.candidates(state, env, state.phase.hex, seat).map((victim) => ({ victim }));
    },
    validate(state, action, env) {
      if (state.phase.t !== 'STEAL_PICK') return reject('WRONG_PHASE');
      if (state.phase.seat !== action.seat) return reject('NOT_YOUR_TURN');
      const victim = action.payload['victim'];
      if (typeof victim !== 'number' || !Number.isInteger(victim)) return reject('BAD_PAYLOAD');
      if (victim === action.seat) return reject('CANNOT_ROB_SELF'); // outline §14.6
      if (!victims.candidates(state, env, state.phase.hex, action.seat).includes(victim)) {
        // an adjacent seat the Friendly Bandit shields is refused for THAT reason —
        // 'NOT_ADJACENT' would be a lie the client would repeat to the table
        return reject(victims.adjacent(state, env, state.phase.hex, action.seat).includes(victim) ? 'VICTIM_PROTECTED' : 'NOT_ADJACENT');
      }
      return OK;
    },
    apply(state, action, env, emit) {
      if (state.phase.t !== 'STEAL_PICK') return;
      const thief = action.seat;
      const victim = Number(action.payload['victim']);
      const hand = state.seats[victim]!.hand;
      const total = bagTotal(hand);
      if (total === 0) {
        emit({ kind: 'STOLEN', vis: 'public', from: victim, to: thief, empty: true }); // nothing, no substitute (outline §14.6)
      } else {
        // plan §9.3 draw: flatten in canonical resource order, one rngInt(len)
        const flat: string[] = [];
        for (const r of env.def.resources) for (let i = 0; i < (hand[r.id] ?? 0); i++) flat.push(r.id);
        const holder = { rng: state.rng };
        const idx = rngInt(holder, flat.length);
        state.rng = holder.rng;
        const resource = flat[idx]!;
        hand[resource] = (hand[resource] ?? 0) - 1;
        if (hand[resource] === 0) delete hand[resource];
        state.seats[thief]!.hand[resource] = (state.seats[thief]!.hand[resource] ?? 0) + 1;
        emit({ kind: 'STOLEN', vis: 'public', from: victim, to: thief, empty: false, privateTo: [thief, victim], privateFields: { resource } });
      }
      enterPhase(state, env, backNode(state, state.phase.back, thief), emit);
    },
  };
}

/** The Bandit's steal step: land-hex neighbours, minus the Friendly Bandit's shield. */
export const stealPickSpec: ActionSpec = makeStealPickSpec({ candidates: stealCandidates, adjacent: adjacentSeats });

export const banditModule: RuleModule = {
  id: 'bandit',
  register(ctx) {
    ctx.configDefaults({ startTile: 'desert' });
    ctx.bag({
      init(env, _rng, moduleCfg) {
        // H20-S1: a sea map names its own start — the desert, "the hex holding the 12", or a cell
        const seaStart = env.map.sea?.banditStart;
        if (seaStart !== undefined) return { hex: banditStartOn(env.board.hexes, seaStart) } satisfies BanditBag;
        const startTile = String(moduleCfg['startTile'] ?? 'desert');
        const candidates = [...env.board.hexes.values()].filter((h) => h.tile === startTile).map((h) => h.id);
        if (candidates.length === 0) throw new StateError(`bandit: no '${startTile}' hex to start on (map validation should have caught this)`);
        candidates.sort();
        return { hex: candidates[0]! } satisfies BanditBag;
      },
      clone(b) {
        const src = b as BanditBag;
        return { hex: src.hex } satisfies BanditBag;
      },
    });

    ctx.phaseState('DISCARD', {
      eligibleWinner: () => null,
      timeoutKey: 'DISCARD',
      // the response window's waiting set (H20-S0c): every seat still owing a discard
      owedSeats: (state) =>
        state.phase.t === 'DISCARD'
          ? Object.entries(state.phase.owed)
              .filter(([, n]) => n > 0)
              .map(([s]) => Number(s))
              .sort((a, b) => a - b)
          : [],
    });
    ctx.phaseState('BANDIT_MOVE', { eligibleWinner: (n) => (n.t === 'BANDIT_MOVE' ? n.seat : null), timeoutKey: 'BANDIT_MOVE' });
    ctx.phaseState('STEAL_PICK', { eligibleWinner: (n) => (n.t === 'STEAL_PICK' ? n.seat : null), timeoutKey: 'STEAL_PICK' });

    // claims 7s ahead of harvest (module order: bandit before harvest? plan §5.2 order is
    // turn-flow, setup, harvest, bandit… — harvest passes on 7 explicitly, so order-safe)
    ctx.onDiceResolved((state, env, d1, d2, emit) => {
      if (d1 + d2 !== 7) return 'pass';
      resolveSevenRoll(state, env, emit);
      return 'handled';
    });

    ctx.action('DISCARD_SELECT', {
      enumerate(state, _env, seat) {
        if (state.phase.t !== 'DISCARD') return null;
        const owe = state.phase.owed[seat];
        return owe === undefined ? null : { descriptor: { count: owe } }; // open payload (plan §8.3)
      },
      validate(state, action, env) {
        if (state.phase.t !== 'DISCARD') return reject('WRONG_PHASE');
        const owe = state.phase.owed[action.seat];
        if (owe === undefined) return reject('NOTHING_OWED');
        const cards = action.payload['cards'];
        if (typeof cards !== 'object' || cards === null || Array.isArray(cards)) return reject('BAD_PAYLOAD');
        let total = 0;
        for (const [rid, n] of Object.entries(cards as Record<string, unknown>)) {
          // R1: every peer bag validator (advanceCards/supplyTrade/playerTrade) checks the id
          // against the def; this one did not, so an Object.prototype key (__proto__/constructor/
          // toString) satisfied `hand[rid] ?? 0` with an INHERITED value and discarded nothing.
          if (!env.def.resources.some((r) => r.id === rid)) return reject('BAD_PAYLOAD');
          if (typeof n !== 'number' || !Number.isInteger(n) || n < 1) return reject('BAD_PAYLOAD');
          if ((state.seats[action.seat]!.hand[rid] ?? 0) < n) return reject('CARDS_NOT_HELD');
          total += n;
        }
        if (total !== owe) return reject('WRONG_DISCARD_COUNT');
        return OK;
      },
      apply(state, action, env, emit) {
        if (state.phase.t !== 'DISCARD') return;
        const cards = action.payload['cards'] as Record<string, number>;
        for (const [rid, n] of Object.entries(cards)) {
          state.seats[action.seat]!.hand[rid] = (state.seats[action.seat]!.hand[rid] ?? 0) - n;
          if (state.seats[action.seat]!.hand[rid] === 0) delete state.seats[action.seat]!.hand[rid];
          state.supply[rid] = (state.supply[rid] ?? 0) + n; // discards return to the Supply (outline §9)
        }
        emit({ kind: 'DISCARDED', vis: 'public', seat: action.seat, bag: { ...cards } }); // physically face-up (D8)
        const owed = { ...state.phase.owed };
        delete owed[action.seat];
        if (Object.keys(owed).length > 0) {
          state.phase = { t: 'DISCARD', seat: state.phase.seat, owed };
        } else {
          enterPhase(state, env, { t: 'BANDIT_MOVE', seat: state.phase.seat, via: 'ROLL7', back: 'ACTION' }, emit); // single pass — outline §14.1
        }
      },
    });

    ctx.action('MOVE_BANDIT', {
      enumerate(state, env, seat) {
        if (state.phase.t !== 'BANDIT_MOVE' || state.phase.seat !== seat) return null;
        return banditTargets(state, env, seat).map((hex) => ({ hex }));
      },
      validate(state, action, env) {
        if (state.phase.t !== 'BANDIT_MOVE') return reject('WRONG_PHASE');
        if (state.phase.seat !== action.seat) return reject('NOT_YOUR_TURN');
        const hex = action.payload['hex'];
        if (typeof hex !== 'string') return reject('BAD_PAYLOAD');
        if (!liveBoard(state, env).hexes.has(hex)) return reject('NO_SUCH_SITE'); // never onto sea or fog (outline §9); a revealed hex is land (H20)
        if (hex === bag(state).hex) return reject('BANDIT_MUST_MOVE'); // outline §14.3
        // D182: shielded hexes are refused, not merely hidden. The client proposes and the
        // server decides (invariant 4), so a hand-written action meets the same rule.
        if (!banditTargets(state, env, action.seat).includes(hex)) return reject('HEX_PROTECTED');
        return OK;
      },
      apply(state, action, env, emit) {
        if (state.phase.t !== 'BANDIT_MOVE') return;
        const hex = String(action.payload['hex']);
        const from = bag(state).hex;
        bag(state).hex = hex;
        emit({ kind: 'BANDIT_MOVED', vis: 'public', seat: action.seat, from, to: hex, via: state.phase.via });
        const victims = stealCandidates(state, env, hex, action.seat);
        if (victims.length > 0) {
          enterPhase(state, env, { t: 'STEAL_PICK', seat: action.seat, hex, via: state.phase.via, back: state.phase.back }, emit);
        } else {
          // no adjacent opponents ⇒ no steal, auto-advance with a narrated why (plan §7.4)
          emit({ kind: 'AUTO_RESOLVED', vis: 'public', seat: action.seat, what: 'STEAL_SKIPPED_NO_VICTIMS' });
          enterPhase(state, env, backNode(state, state.phase.back, action.seat), emit);
        }
      },
    });

    ctx.action('STEAL_PICK', stealPickSpec);
  },
};

export function backNode(state: GameState, back: BackRef, seat: SeatIdx): { t: 'PRE_ROLL'; seat: SeatIdx } | { t: 'ACTION'; seat: SeatIdx } | { t: 'PARTNER_ACTION'; roller: SeatIdx; seat: SeatIdx } {
  if (back === 'PRE_ROLL') return { t: 'PRE_ROLL', seat };
  if (back === 'ACTION') return { t: 'ACTION', seat };
  // during the partner's half the dice STAY with the roller (turn.roller) — the seat here
  // is the partner returning from a militia interlude (plan §7.6; exercised in H1-S11)
  return { t: 'PARTNER_ACTION', roller: state.turn.roller, seat };
}
